import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Loader2,
  Send,
  CheckCircle,
  Trophy,
  IndianRupee,
  Upload,
  X,
  MapPin,
  Calendar,
  Users,
} from 'lucide-react';
import logoImg from '../assets/logo.png';
import { API_BASE_URL, configurationsAPI } from '../services/api';
import { resolveUploadUrl } from '../utils/uploadUrl';
import { DAY_NAMES, formatRupee, formatFeeLine, formatYmdShort, hasConfiguredFee, signupPaymentTotal } from '../features/tournament/tournamentFormUtils';

const API_BASE = API_BASE_URL;

const PAYMENT_CONFIG_DEFAULTS = {
  upiNumber: '',
};

const PublicTournamentRegister = () => {
  const { tournamentId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(PAYMENT_CONFIG_DEFAULTS);

  const [form, setForm] = useState({
    team_name: '',
    team_profile_link: '',
    captain_name: '',
    phone: '',
    alternate_contact_name: '',
    alternate_contact_number: '',
    amount_paid: '',
  });
  const [selectedDates, setSelectedDates] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [qrVisible, setQrVisible] = useState(false);
  const receiptInputRef = useRef(null);

  const requiredDates = info?.availability_required_count ?? 0;
  const availabilityDates = info?.availability_dates ?? [];

  useEffect(() => {
    return () => {
      if (receiptPreview) URL.revokeObjectURL(receiptPreview);
    };
  }, [receiptPreview]);

  useEffect(() => {
    configurationsAPI
      .paymentConfig()
      .then((res) => {
        if (res.success && res.data) setPaymentConfig(res.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setQrVisible(false);
  }, [info?.upi_qr_image_url]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setInfo(null);
        setDone(false);
        const res = await fetch(`${API_BASE}/tournaments/public/${tournamentId}`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Tournament not found');
        }
        if (!cancelled) {
          setInfo(data.data);
          const totalDue = signupPaymentTotal(
            data.data.registration_amount,
            data.data.security_amount
          );
          if (totalDue != null) {
            setForm((f) => ({ ...f, amount_paid: String(totalDue) }));
          }
        }
      } catch (e) {
        if (!cancelled) {
          setInfo(null);
          setError(e.message || 'Failed to load tournament');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  const toggleDate = (ymd) => {
    setSelectedDates((prev) => {
      if (prev.includes(ymd)) return prev.filter((d) => d !== ymd);
      if (prev.length >= requiredDates) return prev;
      return [...prev, ymd];
    });
    setFieldErrors((e) => ({ ...e, availability_dates: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.team_name.trim()) err.team_name = 'Team name is required';
    if (!form.captain_name.trim()) err.captain_name = 'Captain name is required';
    if (!/^\d{10}$/.test(form.phone)) err.phone = 'Enter a valid 10-digit contact number';
    if (!form.alternate_contact_name.trim()) {
      err.alternate_contact_name = 'Alternate contact person is required';
    }
    if (!/^\d{10}$/.test(form.alternate_contact_number)) {
      err.alternate_contact_number = 'Enter a valid 10-digit alternate number';
    }
    if (!form.amount_paid.trim()) err.amount_paid = 'Please enter the amount paid';
    else if (Number.isNaN(parseInt(form.amount_paid, 10)) || parseInt(form.amount_paid, 10) < 0) {
      err.amount_paid = 'Enter a valid amount in rupees';
    }
    if (!receiptFile) err.receipt = 'Payment receipt is required (image or PDF)';
    if (requiredDates > 0 && selectedDates.length !== requiredDates) {
      err.availability_dates = `Select exactly ${requiredDates} dates (${selectedDates.length} selected)`;
    }
    setFieldErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('team_name', form.team_name.trim());
      fd.append('captain_name', form.captain_name.trim());
      fd.append('phone', form.phone);
      fd.append('team_profile_link', form.team_profile_link.trim());
      fd.append('alternate_contact_name', form.alternate_contact_name.trim());
      fd.append('alternate_contact_number', form.alternate_contact_number);
      fd.append('amount_paid', form.amount_paid);
      fd.append('availability_dates', JSON.stringify(selectedDates));
      fd.append('receipt', receiptFile);

      const res = await fetch(`${API_BASE}/tournaments/public/${tournamentId}/registrations`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Submission failed');
      }
      setDone(true);
    } catch (err) {
      setError(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const upiId = info?.upi_id || paymentConfig.upiNumber;
  const qrSrc = info?.upi_qr_image_url ? resolveUploadUrl(info.upi_qr_image_url) : null;

  const fmtMatchRange = () => {
    if (!info?.tournament_starts_at) return null;
    const fmt = (d) =>
      new Date(d).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    return `${fmt(info.tournament_starts_at)} – ${fmt(info.tournament_ends_at)}`;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--ga-bg-page)] p-6">
        <Loader2 className="h-10 w-10 animate-spin text-[var(--ga-primary)]" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--ga-bg-page)] p-6">
        <img src={logoImg} alt="GroundAale" className="mb-6 h-10 opacity-90" />
        <p className="max-w-md text-center font-medium text-red-700">{error}</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--ga-bg-page)] p-6">
        <img src={logoImg} alt="GroundAale" className="mb-6 h-10" />
        <div className="w-full max-w-md rounded-2xl border border-[var(--ga-border-subtle)] bg-[var(--ga-bg-surface)] p-8 text-center shadow-lg">
          <CheckCircle className="mx-auto mb-4 h-14 w-14 text-[var(--ga-primary)]" />
          <h1 className="mb-2 text-xl font-bold text-[var(--ga-text-primary)]">
            Registration submitted
          </h1>
          <p className="mb-6 text-sm text-[var(--ga-text-muted)]">
            {info?.facility_name || 'The venue'} will review your team entry and contact you if
            needed.
          </p>
          <Link
            to="/"
            className="inline-block text-sm font-semibold text-[var(--ga-primary)] hover:underline"
          >
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const spots =
    info?.spots_left != null
      ? `${info.spots_left} spot${info.spots_left === 1 ? '' : 's'} left`
      : null;

  const feeLabel =
    info?.registration_amount != null
      ? formatRupee(info.registration_amount)
      : info?.registration_fee != null
        ? `${formatRupee(info.registration_fee)}/match`
        : 'See below';

  const totalDue = signupPaymentTotal(info?.registration_amount, info?.security_amount);
  const showMorningFee = hasConfiguredFee(info?.registration_fee);
  const showSecondSlotFee = hasConfiguredFee(info?.registration_fee_afternoon);
  const showRegistrationAmount = hasConfiguredFee(info?.registration_amount);
  const showSecurityAmount = hasConfiguredFee(info?.security_amount);
  const showMatchFees = showMorningFee || showSecondSlotFee;
  const showSignupFees = showRegistrationAmount || showSecurityAmount || totalDue != null;

  const feeRow = (label, value, { emphasis = false } = {}) => (
    <div
      key={label}
      className={`flex justify-between gap-4 ${emphasis ? 'pt-1 font-semibold text-[var(--ga-text-primary)]' : 'text-sm text-[var(--ga-text-primary)]'}`}
    >
      <span className={emphasis ? undefined : 'text-[var(--ga-text-muted)]'}>{label}</span>
      <span className={`tabular-nums ${emphasis ? '' : 'font-semibold'}`}>{formatFeeLine(value)}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--ga-bg-page)]">
      <header className="border-b border-[var(--ga-border-subtle)] bg-[var(--ga-bg-surface)]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src={logoImg} alt="GroundAale" className="h-9" />
          </Link>
          <Link
            to="/"
            className="text-sm font-semibold text-[var(--ga-primary)] hover:underline"
          >
            Home
          </Link>
        </div>
      </header>

      <section className="bg-gradient-to-br from-[var(--ga-primary)] to-[var(--ga-brand-navy)] px-4 py-8 text-white">
        <div className="mx-auto max-w-4xl">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-white/80">
            <Trophy className="h-5 w-5" />
            Tournament enrollment
          </div>
          <h1 className="text-2xl font-bold md:text-3xl">{info.name}</h1>
          <h2 className="mt-1 text-lg font-medium text-white/90">{info.facility_name}</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {info.facility_name && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
                <MapPin className="h-3.5 w-3.5" /> {info.facility_name}
              </span>
            )}
            {fmtMatchRange() && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
                <Calendar className="h-3.5 w-3.5" /> {fmtMatchRange()}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
              <IndianRupee className="h-3.5 w-3.5" /> {feeLabel}
            </span>
            {spots && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
                <Users className="h-3.5 w-3.5" /> {spots}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-8">
        {(info.format_details || info.rules_details || info.prizes_details) && (
          <div className="mb-8 grid gap-4 md:grid-cols-3">
            {[
              { title: 'Format', body: info.format_details },
              { title: 'Rules', body: info.rules_details },
              { title: 'Prizes', body: info.prizes_details },
            ].map(
              (col) =>
                col.body && (
                  <div
                    key={col.title}
                    className="rounded-xl border border-[var(--ga-border-subtle)] bg-[var(--ga-bg-surface)] p-4 shadow-sm"
                  >
                    <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--ga-text-muted)]">
                      {col.title}
                    </h3>
                    <p className="whitespace-pre-wrap text-sm text-[var(--ga-text-primary)]">
                      {col.body}
                    </p>
                  </div>
                )
            )}
          </div>
        )}

        <div className="mb-8 rounded-xl border border-[var(--ga-border-subtle)] bg-[var(--ga-bg-surface)] p-5 shadow-sm">
          <div className="flex flex-wrap items-start gap-6">
            <div className="min-w-0 flex-1 space-y-3">
              <p className="text-sm font-semibold text-[var(--ga-text-primary)]">
                Pay the registration amount via UPI
              </p>
              {upiId && (
                <p className="text-sm text-[var(--ga-text-muted)]">
                  UPI ID:{' '}
                  <strong className="tabular-nums text-[var(--ga-text-primary)]">{upiId}</strong>
                </p>
              )}

              {(showMatchFees || showSignupFees) && (
              <div className="space-y-3">
                <p className="text-xs font-bold uppercase tracking-wide text-[var(--ga-text-muted)]">
                  Fees
                </p>
                {showMatchFees && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--ga-text-muted)]">
                      Fee structure per match
                    </p>
                    <div className="mt-2 space-y-1.5">
                      {showMorningFee && feeRow('Morning match', info.registration_fee)}
                      {showSecondSlotFee && feeRow('Second slot', info.registration_fee_afternoon)}
                    </div>
                  </div>
                )}
                {showSignupFees && (
                  <div className={`space-y-1.5 ${showMatchFees ? 'border-t border-[var(--ga-border-subtle)] pt-3' : ''}`}>
                    {showRegistrationAmount && feeRow('Registration amount', info.registration_amount)}
                    {showSecurityAmount && feeRow('Security amount', info.security_amount)}
                    {totalDue != null && feeRow('Total due now', totalDue, { emphasis: true })}
                  </div>
                )}
              </div>
              )}
            </div>
            {qrSrc && (
              <img
                src={qrSrc}
                alt="UPI QR code"
                className={`h-36 w-36 shrink-0 rounded-lg border border-[var(--ga-border-subtle)] bg-white object-contain p-1 md:h-40 md:w-40 ${qrVisible ? '' : 'hidden'}`}
                onLoad={() => setQrVisible(true)}
                onError={() => setQrVisible(false)}
              />
            )}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-2xl border border-[var(--ga-border-subtle)] bg-[var(--ga-bg-surface)] p-6 shadow-md"
        >
          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
              {error}
            </div>
          )}

          {requiredDates > 0 && availabilityDates.length > 0 && (
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <label className="text-xs font-semibold text-[var(--ga-text-muted)]">
                  Match availability * — select {requiredDates} of {availabilityDates.length}{' '}
                  {DAY_NAMES[info.match_day_of_week] || 'match'} dates
                </label>
                <span className="text-xs font-bold text-[var(--ga-primary)]">
                  {selectedDates.length} / {requiredDates} selected
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {availabilityDates.map((ymd) => {
                  const selected = selectedDates.includes(ymd);
                  const atCap = !selected && selectedDates.length >= requiredDates;
                  return (
                    <button
                      key={ymd}
                      type="button"
                      disabled={atCap}
                      onClick={() => toggleDate(ymd)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                        selected
                          ? 'border-[var(--ga-primary)] bg-[var(--ga-primary)] text-white'
                          : atCap
                            ? 'cursor-not-allowed border-[var(--ga-border-subtle)] text-[var(--ga-text-subtle)] opacity-50'
                            : 'border-[var(--ga-border-subtle)] bg-[var(--ga-bg-surface)] text-[var(--ga-text-primary)] hover:border-[var(--ga-primary)]'
                      }`}
                    >
                      {formatYmdShort(ymd)}
                    </button>
                  );
                })}
              </div>
              {fieldErrors.availability_dates && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.availability_dates}</p>
              )}
            </div>
          )}

          <div>
            <h3 className="mb-3 text-sm font-bold text-[var(--ga-text-primary)]">Team details</h3>
            <div className="space-y-4">
              <Field
                label="Team name *"
                value={form.team_name}
                onChange={(v) => setForm((f) => ({ ...f, team_name: v }))}
                error={fieldErrors.team_name}
              />
              <Field
                label="Team profile link"
                value={form.team_profile_link}
                onChange={(v) => setForm((f) => ({ ...f, team_profile_link: v }))}
                placeholder="https://… (optional)"
              />
              <Field
                label="Captain name *"
                value={form.captain_name}
                onChange={(v) => setForm((f) => ({ ...f, captain_name: v }))}
                error={fieldErrors.captain_name}
              />
              <Field
                label="Contact number *"
                value={form.phone}
                onChange={(v) =>
                  setForm((f) => ({ ...f, phone: v.replace(/\D/g, '').slice(0, 10) }))
                }
                inputMode="numeric"
                placeholder="9876543210"
                error={fieldErrors.phone}
              />
              <Field
                label="Alternate contact person *"
                value={form.alternate_contact_name}
                onChange={(v) => setForm((f) => ({ ...f, alternate_contact_name: v }))}
                error={fieldErrors.alternate_contact_name}
              />
              <Field
                label="Alternate contact number *"
                value={form.alternate_contact_number}
                onChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    alternate_contact_number: v.replace(/\D/g, '').slice(0, 10),
                  }))
                }
                inputMode="numeric"
                placeholder="9876543210"
                error={fieldErrors.alternate_contact_number}
              />
              <div>
                <label className="mb-1 block text-xs font-semibold text-[var(--ga-text-muted)]">
                  Amount paid (₹) *
                </label>
                <input
                  className="ga-input w-full"
                  inputMode="numeric"
                  placeholder={
                    totalDue != null
                      ? `e.g. ${totalDue}`
                      : info?.registration_amount != null
                        ? `e.g. ${info.registration_amount}`
                        : 'Enter amount paid'
                  }
                  value={form.amount_paid}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, amount_paid: e.target.value.replace(/\D/g, '') }))
                  }
                />
                {fieldErrors.amount_paid && (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.amount_paid}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-[var(--ga-text-muted)]">
                  Payment receipt *{' '}
                  <span className="font-normal">(image or PDF, max 5 MB)</span>
                </label>
                <input
                  ref={receiptInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setReceiptFile(file || null);
                    setFieldErrors((prev) => ({ ...prev, receipt: undefined }));
                    if (file && file.type.startsWith('image/')) {
                      setReceiptPreview(URL.createObjectURL(file));
                    } else {
                      setReceiptPreview(null);
                    }
                  }}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => receiptInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-lg border border-[var(--ga-border-subtle)] bg-[var(--ga-bg-surface)] px-3 py-2 text-sm font-medium hover:bg-[var(--ga-bg-surface-subtle)]"
                  >
                    <Upload className="h-4 w-4" />
                    {receiptFile ? 'Change file' : 'Choose file'}
                  </button>
                  {receiptFile && (
                    <span className="max-w-[200px] truncate text-sm">{receiptFile.name}</span>
                  )}
                  {receiptFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setReceiptFile(null);
                        setReceiptPreview(null);
                        if (receiptInputRef.current) receiptInputRef.current.value = '';
                      }}
                      className="rounded p-1 hover:bg-[var(--ga-bg-surface-subtle)]"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {receiptPreview && (
                  <img
                    src={receiptPreview}
                    alt="Receipt preview"
                    className="mt-2 max-h-40 rounded-lg border border-[var(--ga-border-subtle)]"
                  />
                )}
                {fieldErrors.receipt && (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.receipt}</p>
                )}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--ga-primary)] py-3 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
            Submit registration
          </button>
        </form>
      </div>
    </div>
  );
};

function Field({ label, value, onChange, error, placeholder, inputMode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-[var(--ga-text-muted)]">
        {label}
      </label>
      <input
        className="ga-input w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default PublicTournamentRegister;
